import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Sparkles, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

type AuthMode = 'login' | 'magic-link' | 'reset-password';

export function LoginPage() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const { signIn, signInWithMagicLink, resetPassword } = useAuth();
  const { actualTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (mode === 'magic-link') {
        const { error } = await signInWithMagicLink(email);
        if (error) {
          setError(error.message);
        } else {
          setEmailSent(true);
        }
      } else if (mode === 'reset-password') {
        const { error } = await resetPassword(email);
        if (error) {
          setError(error.message);
        } else {
          setEmailSent(true);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          navigate(from, { replace: true });
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-obsidian px-4">
        <Card variant="bordered" padding="lg" className="w-full max-w-md">
          <CardContent className="text-center">
            <div className="w-16 h-16 rounded bg-amber/10 border border-amber/20 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-amber" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-obsidian dark:text-signal">
              Check Your Email
            </h1>
            <p className="mt-3 text-slate-500 dark:text-slate-400">
              {mode === 'reset-password' ? (
                <>
                  We've sent a password reset link to <strong className="text-obsidian dark:text-signal">{email}</strong>.
                  Click the link in the email to reset your password.
                </>
              ) : (
                <>
                  We've sent a magic link to <strong className="text-obsidian dark:text-signal">{email}</strong>.
                  Click the link in the email to sign in.
                </>
              )}
            </p>
            <Button
              variant="outline"
              className="mt-6"
              onClick={() => {
                setEmailSent(false);
                setMode('login');
              }}
            >
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-obsidian px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img
            src={actualTheme === 'dark' ? '/logo-icon-dark.svg' : '/logo-icon-light.svg'}
            alt="ChurnLogix"
            className="w-10 h-10"
          />
          <span className="text-xl font-heading font-bold">
            <span className="text-obsidian dark:text-signal">CHURN</span>
            <span className="text-slate-400">LOGIX</span>
          </span>
        </Link>

        <Card variant="bordered" padding="lg">
          <CardContent>
            <h1 className="text-2xl font-heading font-bold text-obsidian dark:text-signal text-center">
              {mode === 'login' && 'Welcome Back'}
              {mode === 'magic-link' && 'Sign In with Email'}
              {mode === 'reset-password' && 'Reset Password'}
            </h1>
            <p className="mt-2 text-slate-500 dark:text-slate-400 text-center text-sm">
              {mode === 'login' && 'Sign in to access your dashboard'}
              {mode === 'magic-link' && "We'll send you a magic link"}
              {mode === 'reset-password' && "We'll send you a reset link"}
            </p>

            {error && (
              <div className="mt-6 p-3 rounded bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                leftIcon={<Mail className="w-4 h-4" />}
                required
              />

              {mode === 'login' && (
                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  leftIcon={<Lock className="w-4 h-4" />}
                  required
                />
              )}

              <Button
                type="submit"
                className="w-full"
                isLoading={isLoading}
                rightIcon={
                  mode === 'magic-link' ? (
                    <Sparkles className="w-4 h-4" />
                  ) : mode === 'reset-password' ? (
                    <KeyRound className="w-4 h-4" />
                  ) : (
                    <ArrowRight className="w-4 h-4" />
                  )
                }
              >
                {mode === 'login' && 'Sign In'}
                {mode === 'magic-link' && 'Send Magic Link'}
                {mode === 'reset-password' && 'Send Reset Link'}
              </Button>
            </form>

            {/* Forgot password link - only show in login mode */}
            {mode === 'login' && (
              <p className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setMode('reset-password')}
                  className="text-sm text-slate-500 hover:text-amber"
                >
                  Forgot your password?
                </button>
              </p>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-white dark:bg-obsidian-50 text-sm text-slate-500">
                  or
                </span>
              </div>
            </div>

            {/* Mode switches */}
            <div className="space-y-3">
              {mode === 'login' && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setMode('magic-link')}
                  leftIcon={<Sparkles className="w-4 h-4" />}
                >
                  Sign in with Magic Link
                </Button>
              )}

              {(mode === 'magic-link' || mode === 'reset-password') && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setMode('login')}
                >
                  Sign in with Password
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Back to home */}
        <p className="mt-6 text-center">
          <Link
            to="/"
            className="text-sm text-slate-500 hover:text-amber"
          >
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
